
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { MissingFieldsAlert } from '@/components/MissingFieldsAlert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Plus,
  Search,
  ChevronRight,
  Trash2,
  DollarSign,
  AlertCircle,
  Clock,
  X,
  Check,
  Upload,
  Download,
  Paperclip,
  Eye,
  ArrowRight,
  User,
  Calendar,
  ExternalLink,
  ListTodo,
  FileClock
} from 'lucide-react';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import AutoSaveRichTextField from '@/components/AutoSaveRichTextField';
import RichTextDisplay from '@/components/RichTextDisplay';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { EntityStatusLogTab } from '@/components/EntityStatusLogTab';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const STAGES = ['Request Received', 'Engage Client', 'Internal Review', 'Proposal Sent', 'Closed'];

const STAGE_COLORS = {
  'Request Received': { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-400', pill: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
  'Engage Client': { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400', pill: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
  'Internal Review': { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/30', dot: 'bg-violet-400', pill: 'bg-violet-500/15 text-violet-400 border-violet-500/25' },
  'Proposal Sent': { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30', dot: 'bg-cyan-400', pill: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25' },
  'Closed': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400', pill: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
};

const stageColor = (stage, current) => {
  const ci = STAGES.indexOf(current), si = STAGES.indexOf(stage);
  if (si < ci) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
  if (si === ci) return 'bg-primary text-primary-foreground border-primary';
  return 'bg-secondary text-muted-foreground border-border';
};

const PROPOSAL_JURISDICTION_OPTIONS = [
  { value: 'nyc', label: 'NYC' },
  { value: 'nassau_county', label: 'Nassau County' },
  { value: 'suffolk_county', label: 'Suffolk County' },
  { value: 'westchester', label: 'Westchester' },
  { value: 'other', label: 'Other' },
];

export default function ProposalsPage() {
  const { user, hasPerm } = useAuth();
  const isAdminOrOwner = user?.role === 'Owner' || user?.role === 'Administrator';
  const canApproveProposals = !!hasPerm('approve_proposals');
  const [proposals, setProposals] = useState([]);
  const [numberPreview, setNumberPreview] = useState(null);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editPropNumber, setEditPropNumber] = useState(false);
  const [editPropNumberVal, setEditPropNumberVal] = useState('');
  const [editPropName, setEditPropName] = useState(false);
  const [editPropNameVal, setEditPropNameVal] = useState('');
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [contactDialog, setContactDialog] = useState(null); // 'primary' | 'billable' | null
  const [newContactForm, setNewContactForm] = useState({ name: '', email: '', phone: '', job_title: '' });
  const [sectionForm, setSectionForm] = useState({ section_name: '', section_number: '', scope_of_work: '', fee: 0, jurisdiction_group: '' });
  const [closeMode, setCloseMode] = useState(null);
  const [closureNotes, setClosureNotes] = useState('');
  const [contractTitle, setContractTitle] = useState('');  const [stageFilter, setStageFilter] = useState(null);
  const [proposalAttachments, setProposalAttachments] = useState([]);
  const [propAttachFormOpen, setPropAttachFormOpen] = useState(false);
  const [propAttachName, setPropAttachName] = useState('');
  const [propAttachUploaded, setPropAttachUploaded] = useState(null);
  const [previewPropAttachment, setPreviewPropAttachment] = useState(null);
  const [stageDescriptions, setStageDescriptions] = useState({});
  const [proposalTodos, setProposalTodos] = useState([]);
  const [todoFormOpen, setTodoFormOpen] = useState(false);
  const [todoForm, setTodoForm] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'Medium' });
  // DD recovery dialog (for legacy proposals advanced past Engage Client before the DD-invoice workflow existed)
  const [recoverDdOpen, setRecoverDdOpen] = useState(false);
  const [recoverDdForm, setRecoverDdForm] = useState({ quote_description: '', quote_amount: '', quote_sent: true });
  const location = useLocation();
  const navigate = useNavigate();

  const fetchProposals = () => api.get('/proposals').then(r => setProposals(r.data));
  const fetchNumberPreview = () => api.get('/proposals/next-number-preview').then(r => setNumberPreview(r.data)).catch(() => {});
  const fetchContacts = () => api.get('/contacts').then(r => setContacts(r.data)).catch(() => {});
  useEffect(() => {
    fetchProposals();
    fetchNumberPreview();
    api.get('/proposal-templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
    fetchContacts();
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/proposals/stage-info').then(r => setStageDescriptions(r.data.descriptions || {})).catch(() => {});
  }, []);

  // Auto-open proposal from Leads pipeline navigation
  useEffect(() => {
    const openId = location.state?.openProposalId;
    if (openId && proposals.length > 0) {
      const match = proposals.find(p => p.id === openId);
      if (match) {
        openDetail(match);
        window.history.replaceState({}, '');
      }
    }
  }, [location.state?.openProposalId, proposals]);

  const openDetail = async (p) => {
    const r = await api.get(`/proposals/${p.id}`);
    setSelected(r.data);
    setEditPropNumber(false);
    setEditPropName(false);
    setDetailOpen(true);
    setTodoFormOpen(false);
    try { const ar = await api.get(`/documents/by-proposal/${p.id}`); setProposalAttachments(ar.data); } catch { setProposalAttachments([]); }
    try { const tr = await api.get(`/tasks?proposal_id=${p.id}`); setProposalTodos(tr.data || []); } catch { setProposalTodos([]); }
  };

  const refreshSelected = useCallback(async () => {
    if (!selected) return;
    const r = await api.get(`/proposals/${selected.id}`);
    setSelected(r.data);
  }, [selected]);

  const fetchProposalAttachments = useCallback(async () => {
    if (!selected?.id) return;
    try { const r = await api.get(`/documents/by-proposal/${selected.id}`); setProposalAttachments(r.data); } catch { setProposalAttachments([]); }
  }, [selected?.id]);

  const fetchProposalTodos = useCallback(async () => {
    if (!selected?.id) return;
    try { const r = await api.get(`/tasks?proposal_id=${selected.id}`); setProposalTodos(r.data || []); } catch { setProposalTodos([]); }
  }, [selected?.id]);

  const createProposalTodo = async () => {
    if (!todoForm.title.trim()) return toast.error('Title required');
    try {
      await api.post('/tasks', { ...todoForm, proposal_id: selected.id });
      toast.success('To-Do created');
      setTodoFormOpen(false);
      setTodoForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'Medium' });
      fetchProposalTodos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating to-do'); }
  };

  const toggleProposalTodo = async (todoId, currentStatus) => {
    const newStatus = currentStatus === 'Completed' ? 'In Progress' : 'Completed';
    try {
      await api.put(`/tasks/${todoId}`, { status: newStatus });
      fetchProposalTodos();
    } catch { toast.error('Error updating to-do'); }
  };

  const advanceStage = async () => {
    try {
      await api.post(`/proposals/${selected.id}/advance`);
      toast.success('Stage advanced');
      await refreshSelected();
      fetchProposals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot advance stage'); }
  };

  const closeProposal = async () => {
    if (!closeMode) return;
    try {
      const payload = { outcome: closeMode, closure_notes: closureNotes };
      if (closeMode === 'Won' && contractTitle.trim()) payload.contract_title = contractTitle.trim();
      const r = await api.post(`/proposals/${selected.id}/close`, payload);
      toast.success(`Proposal closed as ${closeMode}; Contract ${r.data.contract?.contract_number} created`);
      setCloseMode(null);
      setClosureNotes('');
      setContractTitle('');
      setDetailOpen(false);
      fetchProposals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error closing proposal'); }
  };

  const updateField = async (field, value) => {
    try {
      await api.put(`/proposals/${selected.id}`, { [field]: value });
      setSelected(prev => ({ ...prev, [field]: value }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save change');
    }
  };

  const createContactAndLink = async () => {
    const name = newContactForm.name.trim();
    if (!name) return toast.error('Contact name is required');
    try {
      const payload = { ...newContactForm, name };
      if (selected?.client_id) payload.linked_clients = [selected.client_id];
      const r = await api.post('/contacts', payload);
      const fieldName = contactDialog === 'primary' ? 'primary_contact_id' : 'billable_contact_id';
      await api.put(`/proposals/${selected.id}`, { [fieldName]: r.data.id });
      await fetchContacts();
      await refreshSelected();
      setContactDialog(null);
      setNewContactForm({ name: '', email: '', phone: '', job_title: '' });
      toast.success(`${contactDialog === 'primary' ? 'Primary' : 'Billable'} contact created`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating contact'); }
  };

  const savePropNumber = async () => {
    const val = editPropNumberVal.trim();
    if (!val) return toast.error('Proposal number cannot be empty');
    await api.put(`/proposals/${selected.id}`, { proposal_number: val });
    setSelected(prev => ({ ...prev, proposal_number: val }));
    setEditPropNumber(false);
    fetchProposals();
    toast.success('Proposal number updated');
  };

  const savePropName = async () => {
    const val = editPropNameVal.trim();
    if (!val) return toast.error('Name cannot be empty');
    await api.put(`/proposals/${selected.id}`, { name: val });
    setSelected(prev => ({ ...prev, name: val }));
    setEditPropName(false);
    fetchProposals();
    toast.success('Proposal name updated');
  };

  const updateMultipleFields = async (fields) => {
    await api.put(`/proposals/${selected.id}`, fields);
    setSelected(prev => ({ ...prev, ...fields }));
  };

  const addSection = async () => {
    if (!sectionForm.section_name) return toast.error('Section name required');
    if (!sectionForm.fee || sectionForm.fee <= 0) return toast.error('Fee must be > 0');
    try {
      await api.post(`/proposals/${selected.id}/sections`, sectionForm);
      toast.success('Section added');
      setSectionForm({ section_name: '', section_number: '', scope_of_work: '', fee: 0, jurisdiction_group: '' });
      refreshSelected();
    } catch (e) { toast.error('Error adding section'); }
  };

  const deleteSection = async (sid) => {
    await api.delete(`/proposal-sections/${sid}`);
    toast.success('Section removed');
    refreshSelected();
  };

  const selectTemplate = (tmpl) => {
    const t = templates.find(t => t.name === tmpl);
    if (t) setSectionForm({ ...sectionForm, section_name: t.name, scope_of_work: t.scope_of_work, jurisdiction_group: t.jurisdiction_group });
  };

  const totalFees = (selected?.sections || []).reduce((sum, s) => sum + (s.fee || 0), 0);
  const searchFiltered = proposals.filter(p =>
    (p.proposal_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.client_name || '').toLowerCase().includes(search.toLowerCase())
  );
  const stageFiltered = stageFilter ? searchFiltered.filter(p => p.stage === stageFilter) : searchFiltered;

  // Sort by stage order, then by proposal number descending within each stage
  const filtered = [...stageFiltered].sort((a, b) => {
    const sa = STAGES.indexOf(a.stage), sb = STAGES.indexOf(b.stage);
    if (sa !== sb) return sa - sb;
    return (b.proposal_number || '').localeCompare(a.proposal_number || '');
  });

  const stageCounts = {};
  STAGES.forEach(s => { stageCounts[s] = 0; });
  searchFiltered.forEach(p => { if (stageCounts[p.stage] !== undefined) stageCounts[p.stage]++; });

  // Stage-aware helpers
  const stageIdx = selected ? STAGES.indexOf(selected.stage) : -1;
  const isEngageClient = selected?.stage === 'Engage Client';
  const isInternalReview = selected?.stage === 'Internal Review';
  const isProposalSent = selected?.stage === 'Proposal Sent';
  const isClosed = selected?.stage === 'Closed';
  const canShowSections = stageIdx >= 2;
  const canClose = isProposalSent;
  const sectionCount = (selected?.sections || []).length;

  const getAdvanceBlockReason = () => {
    if (!selected) return null;
    // Project Address is required at every stage before advancing
    if (!selected.project_address?.trim()) return 'Project Address is required';
    if (selected.stage === 'Request Received' && !selected.proposal_type) return 'Proposal Type is required';
    if (isEngageClient) {
      if (!selected.confirm_spoke_checked) return 'Must confirm client engagement';
      if (!selected.task_path) return 'Must select a Task Path';
      if (selected.task_path === 'Due Diligence') {
        if (!selected.quote_amount || selected.quote_amount <= 0) return 'Quote amount required';
        if (!selected.quote_sent) return 'Must confirm quote was sent';
        if (!selected.ready_to_invoice) return 'Must mark Ready to Invoice';
      }
    }
    if (isProposalSent) return 'Use Close Won or Close Lost';
    return null;
  };
  const advanceBlockReason = getAdvanceBlockReason();

  // Closing also requires Project Address
  const closeBlockReason = !selected?.project_address?.trim() ? 'Project Address is required' : null;

  const getDefaultTab = () => {
    if (isEngageClient) return 'workflow';
    if (isInternalReview) return 'workflow';
    if (isProposalSent) return 'sections';
    return 'info';
  };

  // ─── Due Diligence panel helpers ─────────────────────────────────────────
  // The DD Quote summary is always shown for DD proposals - even after they
  // advanced past Engage Client - so PMs can see linked invoice status, mark
  // payment received, etc. Recovery is the admin-only path for legacy
  // proposals that never went through the new DD-invoice workflow.
  const isDueDiligence = selected?.task_path === 'Due Diligence';
  const dd = selected?.dd_quote || null;
  const ddInvoiceLinked = !!dd?.invoice_id;
  const needsDdRecovery = isDueDiligence
    && !isEngageClient
    && (!ddInvoiceLinked || !selected?.ready_to_invoice);

  const openRecoverDd = () => {
    setRecoverDdForm({
      quote_description: selected.quote_description || dd?.description || '',
      quote_amount: selected.quote_amount ? String(selected.quote_amount) : (dd?.amount ? String(dd.amount) : ''),
      quote_sent: selected.quote_sent !== false,
    });
    setRecoverDdOpen(true);
  };

  const submitRecoverDd = async () => {
    const amt = parseFloat(recoverDdForm.quote_amount);
    if (!amt || amt <= 0) return toast.error('Quote amount must be greater than zero');
    try {
      const r = await api.post(`/proposals/${selected.id}/admin/recover-dd-quote`, {
        quote_description: recoverDdForm.quote_description || '',
        quote_amount: amt,
        quote_sent: !!recoverDdForm.quote_sent,
      });
      setSelected(r.data);
      setRecoverDdOpen(false);
      fetchProposals();
      toast.success('Due Diligence quote recovered; Draft invoice ready in Invoices');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed');
    }
  };

  // Deadline helpers
  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    return Math.ceil((new Date(deadline) - new Date()) / 86400000);
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="proposals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Proposals<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{proposals.length}</span></h1>
          {numberPreview && (
            <div className="mt-1 flex items-center gap-2 text-[11px] font-mono tabular-nums text-muted-foreground" data-testid="proposal-number-preview">
              <span>
                Current highest:{' '}
                <span className="text-foreground/85" data-testid="proposal-number-current">
                  {numberPreview.highest || 'None yet'}
                </span>
              </span>
              <span aria-hidden className="text-muted-foreground/40">/</span>
              <span>
                Next new:{' '}
                <span className="text-primary" data-testid="proposal-number-next">
                  {numberPreview.next}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search proposals..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border h-9 text-sm" data-testid="proposals-search" />
      </div>

      {/* Pipeline Stage Filter with tooltips */}
      <TooltipProvider delayDuration={200}>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="proposal-stage-filter">
          <button
            onClick={() => setStageFilter(null)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              !stageFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-secondary/50 hover:text-foreground'
            }`}
            data-testid="filter-all"
          >All ({searchFiltered.length})</button>
          {STAGES.map(stage => {
            const colors = STAGE_COLORS[stage];
            return (
              <Tooltip key={stage}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                      stageFilter === stage ? colors.pill : 'bg-card text-muted-foreground border-border hover:bg-secondary/50 hover:text-foreground'
                    }`}
                    data-testid={`filter-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                    {stage} ({stageCounts[stage]})
                  </button>
                </TooltipTrigger>
                {stageDescriptions[stage] && (
                  <TooltipContent side="bottom" className="max-w-[240px] text-xs">
                    <p>{stageDescriptions[stage]}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Proposals Table */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="proposals-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/40 border-b border-border">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2 w-8"></th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Proposal</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Client / Contact</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Stage</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Address</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">PM</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Deadline</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">Sections</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, idx) => {
              const colors = STAGE_COLORS[p.stage] || STAGE_COLORS['Request Received'];
              const daysUntil = getDaysUntilDeadline(p.submission_deadline);
              const isOverdue = daysUntil !== null && daysUntil < 0;
              const isDueSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 5;
              // Show stage divider when stage changes
              const prevStage = idx > 0 ? filtered[idx - 1].stage : null;
              const showDivider = !stageFilter && p.stage !== prevStage;
              return (
                <tr
                  key={p.id}
                  className={`border-b border-border last:border-0 hover:bg-secondary/20 transition-colors cursor-pointer group ${showDivider && idx > 0 ? 'border-t-2 border-t-border' : ''}`}
                  onClick={() => openDetail(p)}
                  data-testid={`proposal-row-${p.id}`}
                >
                  <td className="px-3 py-2.5"><div className={`h-2 w-2 rounded-full ${colors.dot}`} /></td>
                  <td className="px-3 py-2.5">
                    <span className="text-sm font-heading font-semibold tracking-tight group-hover:text-primary transition-colors">{p.proposal_number || 'Draft'}</span>
                    {p.outcome && (
                      <Badge className={`ml-2 text-xs ${p.outcome === 'Won' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : 'bg-destructive/15 text-destructive border-destructive/25'}`}>{p.outcome}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm truncate max-w-[200px]">{p.client_name || p.contact_name || '-'}</p>
                      {p.client_name && p.contact_name && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{p.contact_name}</p>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><Badge className={`text-xs ${colors.pill}`}>{p.stage}</Badge></td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{p.project_address || '-'}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[140px]">{(p.pm_names || []).join(', ') || '-'}</td>
                  <td className="px-3 py-2.5">
                    {p.submission_deadline ? (
                      <span className={`text-xs font-medium ${isOverdue ? 'text-destructive' : isDueSoon ? 'text-amber-400' : 'text-muted-foreground'}`}>
                        {p.submission_deadline}
                        {isOverdue && <span className="text-xs ml-0.5">(overdue)</span>}
                        {isDueSoon && !isOverdue && <span className="text-xs ml-0.5">({daysUntil}d)</span>}
                      </span>
                    ) : <span className="text-xs text-muted-foreground/40">-</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-muted-foreground">{p.section_count || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">No proposals found</div>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="proposal-detail-sheet">
          <SheetHeader className="space-y-1.5">
            {/* Proposal number — 13px DM Mono muted above title */}
            {editPropNumber ? (
              <div className="flex items-center gap-1.5">
                <Input value={editPropNumberVal} onChange={e => setEditPropNumberVal(e.target.value)} className="h-7 w-44 text-sm font-mono bg-background" autoFocus onKeyDown={e => { if (e.key === 'Enter') savePropNumber(); if (e.key === 'Escape') setEditPropNumber(false); }} data-testid="edit-proposal-number-input" />
                <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-400" onClick={savePropNumber}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditPropNumber(false)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <p className="text-sm font-mono text-muted-foreground cursor-pointer hover:text-primary transition-colors w-fit"
                 onClick={() => { setEditPropNumber(true); setEditPropNumberVal(selected?.proposal_number || ''); }}
                 data-testid="proposal-number-editable">{selected?.proposal_number || 'Proposal Detail'}</p>
            )}
            <SheetTitle className="text-[18px] font-medium leading-tight">
              {editPropName ? (
                <span className="flex items-center gap-1.5">
                  <Input value={editPropNameVal} onChange={e => setEditPropNameVal(e.target.value)} className="h-8 text-[18px] font-medium bg-background" autoFocus onKeyDown={e => { if (e.key === 'Enter') savePropName(); if (e.key === 'Escape') setEditPropName(false); }} data-testid="edit-proposal-name-input" />
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-400" onClick={savePropName}><Check className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditPropName(false)}><X className="h-3 w-3" /></Button>
                </span>
              ) : (
                <span className="cursor-pointer hover:text-primary transition-colors"
                      onClick={() => { setEditPropName(true); setEditPropNameVal(selected?.name || ''); }}
                      data-testid="proposal-name-editable">
                  {selected?.name || 'Untitled proposal'}
                </span>
              )}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              {selected?.client_name && <span>{selected.client_name}</span>}
              <MissingFieldsAlert
                fields={[
                  { label: 'Client', present: !!selected?.client_name?.trim() },
                  { label: 'Project Address', present: !!selected?.project_address?.trim() },
                  // Quote Amount is only required for Due Diligence proposals — Research proposals don't carry a quote.
                  ...(selected?.task_path === 'Due Diligence'
                    ? [{ label: 'Quote Amount', present: !!selected?.quote_amount }]
                    : []),
                  { label: 'Contact Name', present: !!selected?.contact_name?.trim() },
                  { label: 'Contact Email', present: !!selected?.contact_email?.trim() },
                ]}
                testId="proposal-missing-fields"
              />
            </SheetDescription>
          </SheetHeader>

          {selected && (
            <div className="mt-4 space-y-5">
              {/* Lead link — opens the lead's detail panel via deep-link */}
              {selected.lead_id && selected.lead_name && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground p-2 rounded-md border border-border bg-secondary/20">
                  <ArrowRight className="h-3 w-3" />
                  <span>From Lead:</span>
                  <button
                    className="text-primary hover:underline font-medium flex items-center gap-0.5"
                    onClick={() => { setDetailOpen(false); navigate(`/leads?lead=${selected.lead_id}`); }}
                    data-testid="lead-link"
                  >
                    {selected.lead_name}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}

              {/* Pipeline Bar with tooltips */}
              <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-1" data-testid="proposal-pipeline">
                  {STAGES.map((stage, i) => (
                    <div key={stage} className="flex items-center flex-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`flex-1 text-center py-1.5 px-2 rounded text-xs font-semibold border cursor-help ${stageColor(stage, selected.stage)}`}>
                            {stage}
                          </div>
                        </TooltipTrigger>
                        {stageDescriptions[stage] && (
                          <TooltipContent side="bottom" className="max-w-[220px] text-xs"><p>{stageDescriptions[stage]}</p></TooltipContent>
                        )}
                      </Tooltip>
                      {i < STAGES.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5 shrink-0" />}
                    </div>
                  ))}
                </div>
              </TooltipProvider>

              {/* Current stage description */}
              {stageDescriptions[selected.stage] && (
                <p className="text-xs text-muted-foreground px-1 -mt-3" data-testid="proposal-stage-description">
                  {stageDescriptions[selected.stage]}
                </p>
              )}

              {/* Client + Project Address (inline editable - replaces concatenated header) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-border bg-secondary/15 p-3" data-testid="proposal-header-edit">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Client</Label>
                  <Select value={selected.client_id || ''} onValueChange={v => updateField('client_id', v)} disabled={isClosed}>
                    <SelectTrigger className="bg-background border-border h-9 text-sm mt-0.5" data-testid="proposal-client-header-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {/* If the proposal's linked client is inactive (filtered out of /clients), show it anyway so the value renders. */}
                      {selected.client_id && !clients.some(c => c.id === selected.client_id) && selected.client_name && (
                        <SelectItem value={selected.client_id} className="text-amber-400">
                          {selected.client_name} (inactive)
                        </SelectItem>
                      )}
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Project Address <span className="text-destructive">*</span></Label>
                  <Input value={selected.project_address || ''} onChange={e => setSelected(prev => ({ ...prev, project_address: e.target.value }))} onBlur={e => updateField('project_address', e.target.value)} className="bg-background border-border h-9 text-sm mt-0.5" placeholder="Project address (required)" disabled={isClosed} data-testid="proposal-project-address-header-input" />
                </div>
              </div>

              {/* Action Buttons */}
              {!isClosed && (
                <div className="flex gap-2 flex-wrap items-center">
                  {!isProposalSent && (
                    <Button size="sm" onClick={advanceStage} className="bg-primary text-primary-foreground" disabled={!!advanceBlockReason} data-testid="advance-stage-btn">
                      Advance Stage
                    </Button>
                  )}
                  {canClose && !closeMode && canApproveProposals && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setCloseMode('Won'); setClosureNotes(''); setContractTitle(`Contract for ${selected.contact_name || 'Unknown'}`); }} className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" disabled={sectionCount === 0 || !!closeBlockReason} data-testid="close-won-btn">Close Won</Button>
                      <Button size="sm" variant="outline" onClick={() => { setCloseMode('Lost'); setClosureNotes(''); setContractTitle(''); }} className="text-destructive border-destructive/30 hover:bg-destructive/10" disabled={sectionCount === 0 || !!closeBlockReason} data-testid="close-lost-btn">Close Lost</Button>
                    </>
                  )}
                  {canClose && !closeMode && !canApproveProposals && (
                    <span className="text-xs text-muted-foreground" data-testid="close-perm-denied">You don&apos;t have permission to close proposals.</span>
                  )}
                  {advanceBlockReason && !isProposalSent && (
                    <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="advance-block-reason">
                      <AlertCircle className="h-3 w-3" /> {advanceBlockReason}
                    </span>
                  )}
                  {isProposalSent && closeBlockReason && (
                    <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="close-block-reason-address">
                      <AlertCircle className="h-3 w-3" /> {closeBlockReason}
                    </span>
                  )}
                  {isProposalSent && sectionCount === 0 && (
                    <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="close-block-reason">
                      <AlertCircle className="h-3 w-3" /> Add at least one section before closing
                    </span>
                  )}
                </div>
              )}

              {/* Close Confirmation */}
              {closeMode && (
                <Card className={`border shadow-none ${closeMode === 'Won' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-destructive/30 bg-destructive/5'}`} data-testid="close-confirmation-panel">
                  <CardContent className="p-4 space-y-3">
                    <h3 className={`text-sm font-heading font-bold ${closeMode === 'Won' ? 'text-emerald-400' : 'text-destructive'}`}>Close as {closeMode}</h3>
                    {closeMode === 'Won' && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Contract Title *</Label>
                        <Input value={contractTitle} onChange={e => setContractTitle(e.target.value)} placeholder="Enter contract title" className="bg-background border-border" data-testid="contract-title-input" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-muted-foreground">{closeMode === 'Won' ? 'Win' : 'Loss'} Notes (optional)</Label>
                      <RichTextEditor value={closureNotes} onChange={v => setClosureNotes(v)} placeholder={`Add ${closeMode === 'Won' ? 'win' : 'loss'} notes...`} collapsedHeight={80} data-testid="closure-notes-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={closeProposal} className={closeMode === 'Won' ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'} data-testid="confirm-close-btn">Confirm Close {closeMode}</Button>
                      <Button size="sm" variant="outline" onClick={() => setCloseMode(null)} data-testid="cancel-close-btn">Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Tabs defaultValue={getDefaultTab()} key={selected.id + selected.stage}>
                <TabsList>
                  <TabsTrigger value="info" className="text-xs">Info</TabsTrigger>
                  {(isEngageClient || isInternalReview) && (
                    <TabsTrigger value="workflow" className="text-xs">
                      {isEngageClient ? 'Engage Client' : 'Review'}
                    </TabsTrigger>
                  )}
                  {/* DD Status tab - always visible for DD proposals past Engage Client so PMs
                      can see the linked invoice, payment status, and trigger admin recovery. */}
                  {isDueDiligence && !isEngageClient && (
                    <TabsTrigger value="dd-status" className="text-xs" data-testid="tab-dd-status">
                      <DollarSign className="h-3 w-3 mr-1" /> Due Diligence
                      {needsDdRecovery && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-400" title="Needs recovery" />}
                    </TabsTrigger>
                  )}
                  {canShowSections && (
                    <TabsTrigger value="sections" className="text-xs">Sections ({sectionCount})</TabsTrigger>
                  )}
                  {/* Show review checkboxes tab at Proposal Sent */}
                  {isProposalSent && (
                    <TabsTrigger value="sign-off" className="text-xs">Sign-Off</TabsTrigger>
                  )}
                  {!isClosed && (
                    <TabsTrigger value="todos" className="text-xs" data-testid="tab-proposal-todos">
                      <ListTodo className="h-3 w-3 mr-1" /> To-Dos{proposalTodos.length > 0 ? ` (${proposalTodos.filter(t => t.status !== 'Completed').length})` : ''}
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="time-logged" className="text-xs" data-testid="tab-time-logged">
                    <Clock className="h-3 w-3 mr-1" /> Time ({selected.total_hours_logged || 0}h)
                  </TabsTrigger>
                  <TabsTrigger value="status-log" className="text-xs" data-testid="tab-proposal-status-log">
                    <FileClock className="h-3 w-3 mr-1" /> Status Log{(selected.status_log || []).length > 0 ? ` (${(selected.status_log || []).length})` : ''}
                  </TabsTrigger>
                  <TabsTrigger value="attachments" className="text-xs" data-testid="tab-proposal-attachments">
                    <Paperclip className="h-3 w-3 mr-1" /> Files{proposalAttachments.length > 0 ? ` (${proposalAttachments.length})` : ''}
                  </TabsTrigger>
                </TabsList>

                {/* Info Tab */}
                <TabsContent value="info" className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Type *</Label>
                      <Select value={selected.proposal_type || ''} onValueChange={v => updateField('proposal_type', v)}>
                        <SelectTrigger className={`bg-background border-border ${!selected.proposal_type && selected.stage === 'Request Received' ? 'border-amber-500/50' : ''}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {['Municipal', 'Developer/Architect', 'Residential'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!selected.proposal_type && selected.stage === 'Request Received' && (
                        <p className="text-xs text-amber-400 mt-0.5">Required before advancing</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Jurisdiction</Label>
                      {(() => {
                        const jurArr = Array.isArray(selected.jurisdiction) ? selected.jurisdiction : [];
                        const legacyStr = (!Array.isArray(selected.jurisdiction) && typeof selected.jurisdiction === 'string' && selected.jurisdiction.trim()) ? selected.jurisdiction.trim() : '';
                        const toggle = (val) => {
                          const next = jurArr.includes(val) ? jurArr.filter(v => v !== val) : [...jurArr, val];
                          updateField('jurisdiction', next);
                        };
                        return (
                          <div data-testid="proposal-jurisdiction">
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {PROPOSAL_JURISDICTION_OPTIONS.map(opt => {
                                const sel = jurArr.includes(opt.value);
                                return (
                                  <button key={opt.value} type="button" disabled={isClosed} onClick={() => toggle(opt.value)}
                                    className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/50'} ${isClosed ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    data-testid={`proposal-jurisdiction-${opt.value}`}>
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                            {jurArr.includes('other') && (
                              <Input value={selected.jurisdiction_other || ''} onChange={e => setSelected(prev => ({ ...prev, jurisdiction_other: e.target.value }))} onBlur={e => updateField('jurisdiction_other', e.target.value)} placeholder="Specify the other jurisdiction" className="bg-background border-border h-8 text-xs mt-1.5" disabled={isClosed} data-testid="proposal-jurisdiction-other" />
                            )}
                            {legacyStr && jurArr.length === 0 && (
                              <p className="text-xs text-muted-foreground mt-1" data-testid="proposal-jurisdiction-legacy">Current value: <span className="text-foreground">{legacyStr}</span> <span className="italic">(select above to update)</span></p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <Select value={selected.client_id || ''} onValueChange={v => updateField('client_id', v)}>
                        <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select client" /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {selected.client_id && !clients.some(c => c.id === selected.client_id) && selected.client_name && (
                            <SelectItem value={selected.client_id} className="text-amber-400">{selected.client_name} (inactive)</SelectItem>
                          )}
                          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Project Manager</Label>
                      <Select value={(selected.project_manager_ids || [])[0] || selected.project_manager_id || ''} onValueChange={v => updateField('project_manager_ids', [v])}>
                        <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select PM" /></SelectTrigger>
                        <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-muted-foreground">Retainer Amount</Label><Input type="number" value={selected.retainer_amount || 0} onChange={e => updateField('retainer_amount', parseFloat(e.target.value) || 0)} className="bg-background border-border" /></div>
                    <div><Label className="text-xs text-muted-foreground">Submission Deadline</Label><Input type="date" value={selected.submission_deadline || ''} onChange={e => updateField('submission_deadline', e.target.value)} className="bg-background border-border" /></div>
                  </div>
                  <div><Label className="text-xs text-muted-foreground">Contact</Label><Input value={selected.contact_name || ''} onChange={e => updateField('contact_name', e.target.value)} className="bg-background border-border" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-muted-foreground">Contact Email</Label><Input value={selected.contact_email || ''} onChange={e => updateField('contact_email', e.target.value)} className="bg-background border-border" /></div>
                    <div><Label className="text-xs text-muted-foreground">Contact Phone</Label><Input value={selected.contact_phone || ''} onChange={e => updateField('contact_phone', e.target.value)} className="bg-background border-border" /></div>
                  </div>

                  {/* Primary + Billable contacts from contacts table - will transfer to contract on close */}
                  <div className="pt-2 border-t border-border space-y-3">
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Linked Contacts</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <Label className="text-xs text-muted-foreground">Primary Contact</Label>
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-primary" onClick={() => { setContactDialog('primary'); setNewContactForm({ name: '', email: '', phone: '', job_title: '' }); }} data-testid="add-primary-contact-btn"><Plus className="h-2.5 w-2.5 mr-0.5" />New</Button>
                        </div>
                        <Select value={selected.primary_contact_id || ''} onValueChange={v => updateField('primary_contact_id', v || null)}>
                          <SelectTrigger className="bg-background border-border" data-testid="primary-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                          <SelectContent className="bg-card border-border max-h-60">
                            {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.job_title ? ` · ${c.job_title}` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {selected.primary_contact_id && (
                          <div className="text-xs text-muted-foreground mt-1 space-x-2">
                            {selected.primary_contact_email && <span>{selected.primary_contact_email}</span>}
                            {selected.primary_contact_phone && <span>{selected.primary_contact_phone}</span>}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <Label className="text-xs text-muted-foreground">Billable Contact</Label>
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-primary" onClick={() => { setContactDialog('billable'); setNewContactForm({ name: '', email: '', phone: '', job_title: '' }); }} data-testid="add-billable-contact-btn"><Plus className="h-2.5 w-2.5 mr-0.5" />New</Button>
                        </div>
                        <Select value={selected.billable_contact_id || ''} onValueChange={v => updateField('billable_contact_id', v || null)}>
                          <SelectTrigger className="bg-background border-border" data-testid="billable-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                          <SelectContent className="bg-card border-border max-h-60">
                            {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.job_title ? ` · ${c.job_title}` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {selected.billable_contact_id && (
                          <div className="text-xs text-muted-foreground mt-1 space-x-2">
                            {selected.billable_contact_email && <span>{selected.billable_contact_email}</span>}
                            {selected.billable_contact_phone && <span>{selected.billable_contact_phone}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div><Label className="text-xs text-muted-foreground">Scope Notes</Label><AutoSaveRichTextField initialValue={selected.notes_scope || ''} resetKey={selected.id} onSave={v => updateField('notes_scope', v)} placeholder="Scope notes..." /></div>
                  {selected.task_path && (
                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">Task Path: {selected.task_path}</Badge>
                        {selected.task_path === 'Due Diligence' && selected.quote_amount > 0 && (
                          <Badge variant="outline" className="text-xs">Quote: ${selected.quote_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Due Diligence Status Tab - always visible for DD proposals past Engage Client.
                    Read-only summary + Admin recovery action for pre-update legacy proposals. */}
                {isDueDiligence && !isEngageClient && (
                  <TabsContent value="dd-status" className="space-y-3 mt-3" data-testid="dd-status-content">
                    <Card className="border-border bg-background shadow-none">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-heading font-bold">Due Diligence Quote</h3>
                            <p className="text-xs text-muted-foreground">Snapshot of the DD billing flow. Edits happen automatically as the linked invoice progresses.</p>
                          </div>
                          {dd?.status === 'paid' && (
                            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border" data-testid="dd-paid-badge">Paid</Badge>
                          )}
                          {dd?.status === 'invoiced' && !dd?.paid_at && (
                            <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 border" data-testid="dd-invoiced-badge">Invoiced, Awaiting Payment</Badge>
                          )}
                          {!ddInvoiceLinked && (
                            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 border" data-testid="dd-unbilled-badge">{dd?.cancelled_invoice_number ? 'Previous Invoice Cancelled' : 'Not Yet Invoiced'}</Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quote Description</Label>
                            <p className="text-sm mt-0.5">{selected.quote_description || dd?.description || <span className="text-muted-foreground italic">- not set -</span>}</p>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quote Amount</Label>
                            <p className="text-sm mt-0.5 font-mono">{(selected.quote_amount || dd?.amount) ? `$${(selected.quote_amount || dd?.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-muted-foreground italic">- not set -</span>}</p>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quote Sent</Label>
                            <p className="text-sm mt-0.5">{selected.quote_sent ? 'Yes' : 'No'}</p>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ready to Invoice</Label>
                            <p className="text-sm mt-0.5">{selected.ready_to_invoice ? 'Yes' : 'No'}</p>
                          </div>
                          {dd?.invoice_number && (
                            <div className="col-span-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Linked Invoice</Label>
                              <p className="text-sm mt-0.5 font-mono">
                                <button
                                  className="text-primary hover:underline inline-flex items-center gap-1"
                                  onClick={() => navigate(`/invoices?id=${dd.invoice_id}`)}
                                  data-testid="dd-open-invoice-btn"
                                >
                                  {dd.invoice_number}
                                  <ExternalLink className="h-3 w-3" />
                                </button>
                              </p>
                            </div>
                          )}
                          {dd?.paid_at && (
                            <div className="col-span-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payment Received</Label>
                              <p className="text-sm mt-0.5">{new Date(dd.paid_at).toLocaleString()}</p>
                            </div>
                          )}
                          {dd?.cancelled_invoice_number && !ddInvoiceLinked && (
                            <div className="col-span-2 p-2 rounded border border-rose-500/20 bg-rose-500/5" data-testid="dd-cancelled-breadcrumb">
                              <Label className="text-xs uppercase tracking-wider text-rose-300">Previous Invoice Cancelled</Label>
                              <p className="text-xs mt-1">
                                <span className="font-mono text-rose-300">{dd.cancelled_invoice_number}</span>
                                {dd.cancelled_at && <span className="text-muted-foreground"> · {new Date(dd.cancelled_at).toLocaleDateString()}</span>}
                                {dd.cancellation_reason && <span className="text-muted-foreground"> · {dd.cancellation_reason}</span>}
                              </p>
                              {dd.cancelled_invoice_id && (
                                <button
                                  className="text-xs text-rose-300 hover:underline mt-1 inline-flex items-center gap-1"
                                  onClick={() => navigate(`/invoices?id=${dd.cancelled_invoice_id}`)}
                                  data-testid="dd-view-cancelled-invoice"
                                >
                                  View cancelled invoice <ExternalLink className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {needsDdRecovery && (
                          <div className="mt-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/5" data-testid="dd-recovery-banner">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-amber-300">Pre-update proposal: recovery needed</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  This Due Diligence proposal advanced past the Engage Client stage
                                  before the DD-invoice workflow existed. {ddInvoiceLinked
                                    ? 'A linked invoice exists, but the proposal still needs the quote fields synced so payments will flow back automatically.'
                                    : 'No DD invoice has been created yet; billing for the research work is blocked.'}
                                </p>
                                {isAdminOrOwner ? (
                                  <Button
                                    size="sm"
                                    className="mt-2 bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30"
                                    onClick={openRecoverDd}
                                    data-testid="dd-recover-btn"
                                  >
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    {ddInvoiceLinked ? 'Sync DD Quote Fields' : 'Recover DD Quote & Generate Invoice'}
                                  </Button>
                                ) : (
                                  <p className="text-xs text-muted-foreground mt-2 italic">Ask an Owner or Administrator to recover the DD quote; they&apos;ll see a &quot;Recover&quot; button here.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {/* Engage Client Workflow Tab */}
                {isEngageClient && (
                  <TabsContent value="workflow" className="space-y-4 mt-3">
                    <Card className="border-border bg-background shadow-none">
                      <CardContent className="p-4 space-y-4">
                        <h3 className="text-sm font-heading font-bold">Engage Client</h3>
                        <div className="flex items-center gap-3 p-3 rounded border border-border bg-card">
                          <Checkbox checked={!!selected.confirm_spoke_checked} onCheckedChange={(v) => updateField('confirm_spoke_checked', !!v)} data-testid="confirm-spoke-checkbox" />
                          <div>
                            <Label className="text-sm font-medium">Client Engaged</Label>
                            <p className="text-xs text-muted-foreground">Confirm that contact has been made with the client</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Task Path</Label>
                          <p className="text-xs text-muted-foreground">Choose how the proposal formulation will proceed</p>
                          <div className="grid grid-cols-2 gap-3">
                            <Card className={`border shadow-none cursor-pointer transition-colors ${selected.task_path === 'Research' ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-secondary/30'}`} onClick={() => updateMultipleFields({ task_path: 'Research', quote_amount: 0, quote_sent: false, ready_to_invoice: false, payment_received: false })} data-testid="task-path-research">
                              <CardContent className="p-3 text-center"><p className="text-sm font-medium">Research</p><p className="text-xs text-muted-foreground mt-1">No charge to client</p></CardContent>
                            </Card>
                            <Card className={`border shadow-none cursor-pointer transition-colors ${selected.task_path === 'Due Diligence' ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-secondary/30'}`} onClick={() => updateField('task_path', 'Due Diligence')} data-testid="task-path-due-diligence">
                              <CardContent className="p-3 text-center"><p className="text-sm font-medium">Due Diligence</p><p className="text-xs text-muted-foreground mt-1">Requires quote & payment</p></CardContent>
                            </Card>
                          </div>
                        </div>
                        {selected.task_path === 'Due Diligence' && (
                          <div className="space-y-3 p-3 rounded border border-amber-500/20 bg-amber-500/5">
                            <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Due Diligence Requirements</h4>
                            {selected.dd_quote && (selected.dd_quote.invoice_id || selected.dd_quote.status !== 'draft') && (
                              <Card className="border-amber-500/40 bg-background shadow-none" data-testid="dd-quote-card">
                                <CardContent className="p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">DD Quote</p>
                                      <p className="text-sm font-medium mt-0.5">{selected.dd_quote.description || 'Due Diligence Research'}</p>
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {selected.dd_quote.invoice_number && (
                                          <>Linked invoice: <a href={`/invoices?focus=${selected.dd_quote.invoice_id}`} className="text-primary hover:underline">{selected.dd_quote.invoice_number}</a></>
                                        )}
                                      </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-lg font-heading font-bold text-amber-400">${(selected.dd_quote.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                      {selected.dd_quote.status === 'paid' && <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400 mt-1"><Check className="h-2.5 w-2.5 mr-1" />Paid {selected.dd_quote.paid_at && `· ${String(selected.dd_quote.paid_at).slice(0, 10)}`}</Badge>}
                                      {selected.dd_quote.status === 'invoiced' && <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400 mt-1">Invoiced · Awaiting Payment</Badge>}
                                      {selected.dd_quote.status === 'draft' && <Badge variant="outline" className="text-xs border-muted text-muted-foreground mt-1">Draft</Badge>}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                            <div><Label className="text-xs text-muted-foreground">Quote Description</Label><Input value={selected.quote_description || ''} onChange={e => updateField('quote_description', e.target.value)} placeholder="Default: Due Diligence Research Fee for [Proposal #]" className="bg-background border-border" data-testid="quote-description-input" /><p className="text-xs text-muted-foreground mt-1">Appears as the line item on the DD invoice. Edits sync to the Draft invoice automatically.</p></div>
                            <div><Label className="text-xs text-muted-foreground">Quote Amount ($)</Label><Input type="number" value={selected.quote_amount || ''} onChange={e => updateField('quote_amount', parseFloat(e.target.value) || 0)} placeholder="Enter quote amount" className="bg-background border-border" data-testid="quote-amount-input" /></div>
                            <div className="flex items-center gap-3 p-2 rounded border border-border bg-card"><Checkbox checked={!!selected.quote_sent} onCheckedChange={(v) => updateField('quote_sent', !!v)} disabled={!!selected.ready_to_invoice} data-testid="quote-sent-checkbox" /><div><Label className="text-sm">Quote Sent to Client</Label><p className="text-xs text-muted-foreground">Confirm the quote was delivered to the client{selected.ready_to_invoice ? ' · locked after Ready to Invoice' : ''}</p></div></div>
                            <div className="flex items-center gap-3 p-2 rounded border border-border bg-card"><Checkbox checked={!!selected.ready_to_invoice} onCheckedChange={(v) => updateField('ready_to_invoice', !!v)} disabled={!selected.quote_sent || !!selected.ready_to_invoice} data-testid="ready-to-invoice-checkbox" /><div><Label className={`text-sm ${!selected.quote_sent ? 'text-muted-foreground' : ''}`}>Ready to Invoice</Label><p className="text-xs text-muted-foreground">Auto-generates a Draft invoice for the quote amount and notifies Owners / Office Managers. One-way toggle.</p></div></div>
                            <div className="flex items-center gap-3 p-2 rounded border border-border bg-card/40"><Checkbox checked={!!selected.payment_received} disabled data-testid="payment-received-checkbox" /><div><Label className="text-sm text-muted-foreground">Payment Received</Label><p className="text-xs text-muted-foreground">Auto-updates when the linked Due Diligence invoice is marked fully Paid</p></div></div>
                          </div>
                        )}
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground">
                            {!selected.confirm_spoke_checked && 'Step 1: Confirm you engaged the client'}
                            {selected.confirm_spoke_checked && !selected.task_path && 'Step 2: Select a Task Path'}
                            {selected.confirm_spoke_checked && selected.task_path === 'Research' && 'Ready to advance to Internal Review'}
                            {selected.confirm_spoke_checked && selected.task_path === 'Due Diligence' && !selected.quote_amount && 'Step 3: Enter the quote amount'}
                            {selected.confirm_spoke_checked && selected.task_path === 'Due Diligence' && selected.quote_amount > 0 && !selected.quote_sent && 'Step 4: Confirm quote was sent'}
                            {selected.confirm_spoke_checked && selected.task_path === 'Due Diligence' && selected.quote_sent && !selected.ready_to_invoice && 'Step 5: Mark Ready to Invoice (generates Draft invoice)'}
                            {selected.confirm_spoke_checked && selected.task_path === 'Due Diligence' && selected.ready_to_invoice && 'Ready to advance to Internal Review · Payment Received will auto-update when invoice is fully paid'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {/* Internal Review Workflow Tab */}
                {isInternalReview && (
                  <TabsContent value="workflow" className="space-y-4 mt-3">
                    <Card className="border-border bg-background shadow-none">
                      <CardContent className="p-4 space-y-4">
                        <h3 className="text-sm font-heading font-bold">Internal Review</h3>
                        <div className="flex items-center gap-3 p-3 rounded border border-border bg-card"><Checkbox checked={!!selected.proposal_formulated} onCheckedChange={(v) => updateField('proposal_formulated', !!v)} data-testid="proposal-formulated-checkbox" /><div><Label className="text-sm font-medium">Proposal Formulated</Label><p className="text-xs text-muted-foreground">Confirm the proposal has been written and reviewed</p></div></div>
                        {selected.task_path && (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">Task Path: {selected.task_path}</Badge>
                            {selected.task_path === 'Due Diligence' && <Badge variant="outline" className="text-xs">Quote: ${(selected.quote_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">Add proposal sections in the &quot;Sections&quot; tab to build out the proposal details.</p>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {/* Sections Tab */}
                {canShowSections && (
                  <TabsContent value="sections" className="mt-3 space-y-3">
                    <Card className="bg-primary/5 border-primary/20 shadow-none"><CardContent className="p-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" /><span className="text-sm font-heading font-bold">Total Fees: ${totalFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span><span className="text-xs text-muted-foreground ml-auto">{sectionCount} section{sectionCount !== 1 ? 's' : ''}</span></CardContent></Card>
                    {(selected.sections || []).map(s => (
                      <Card key={s.id} className="border-border bg-background shadow-none">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div>{s.section_number && <Badge variant="outline" className="text-xs font-mono mr-2">{s.section_number}</Badge>}<span className="text-sm font-medium">{s.section_name}</span>{s.jurisdiction_group && <p className="text-xs text-muted-foreground mt-0.5">{s.jurisdiction_group}</p>}</div>
                            <div className="flex items-center gap-2"><span className="text-sm font-heading font-bold text-primary">${(s.fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>{!isClosed && <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteSection(s.id)} data-testid={`delete-section-${s.id}`}><Trash2 className="h-3 w-3" /></Button>}</div>
                          </div>
                          {s.scope_of_work && <div className="mt-2"><RichTextDisplay value={s.scope_of_work} collapsedHeight={36} /></div>}
                        </CardContent>
                      </Card>
                    ))}
                    {!isClosed && (
                      <Card className="border-border border-dashed bg-transparent shadow-none">
                        <CardContent className="p-3 space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground">Add Section</p>
                          <Select value={sectionForm.section_name} onValueChange={selectTemplate}><SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select template or type custom" /></SelectTrigger><SelectContent className="bg-card border-border max-h-60">{templates.map(t => <SelectItem key={t.id} value={t.name}>{t.name} ({t.jurisdiction_group})</SelectItem>)}</SelectContent></Select>
                          <Input placeholder="Or custom section name" value={sectionForm.section_name} onChange={e => setSectionForm({ ...sectionForm, section_name: e.target.value })} className="bg-background border-border" data-testid="section-name-input" />
                          <Input placeholder="Section # (as on contract)" value={sectionForm.section_number} onChange={e => setSectionForm({ ...sectionForm, section_number: e.target.value })} className="bg-background border-border" data-testid="section-number-input" />
                          <Input type="number" placeholder="Fee ($)" value={sectionForm.fee || ''} onChange={e => setSectionForm({ ...sectionForm, fee: parseFloat(e.target.value) || 0 })} className="bg-background border-border" data-testid="section-fee-input" />
                          <RichTextEditor placeholder="Scope of work" value={sectionForm.scope_of_work} onChange={v => setSectionForm({ ...sectionForm, scope_of_work: v })} collapsedHeight={80} />
                          <Button size="sm" onClick={addSection} className="bg-primary text-primary-foreground" data-testid="add-section-btn"><Plus className="h-3 w-3 mr-1" /> Add Section</Button>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>
                )}

                {/* Sign-Off Tab (at Proposal Sent stage) */}
                {isProposalSent && (
                  <TabsContent value="sign-off" className="mt-3 space-y-3">
                    <Card className="border-border bg-background shadow-none">
                      <CardContent className="p-4 space-y-4">
                        <h3 className="text-sm font-heading font-bold">Internal Sign-Off</h3>
                        <p className="text-xs text-muted-foreground">Optional review checkboxes before closing the proposal.</p>
                        <div className="flex items-center gap-3 p-3 rounded border border-border bg-card">
                          <Checkbox checked={!!selected.jp_reviewed} onCheckedChange={(v) => updateField('jp_reviewed', !!v)} data-testid="jp-reviewed-checkbox" />
                          <div><Label className="text-sm font-medium">JP Reviewed</Label><p className="text-xs text-muted-foreground">JP has reviewed the proposal</p></div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded border border-border bg-card">
                          <Checkbox checked={!!selected.td_reviewed} onCheckedChange={(v) => updateField('td_reviewed', !!v)} data-testid="td-reviewed-checkbox" />
                          <div><Label className="text-sm font-medium">TD Reviewed</Label><p className="text-xs text-muted-foreground">TD has reviewed the proposal</p></div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {/* To-Dos Tab */}
                {!isClosed && (
                  <TabsContent value="todos" className="mt-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{proposalTodos.filter(t => t.status !== 'Completed').length} active to-do{proposalTodos.filter(t => t.status !== 'Completed').length !== 1 ? 's' : ''}</p>
                      <Button size="sm" onClick={() => setTodoFormOpen(!todoFormOpen)} className="bg-primary text-primary-foreground text-xs" data-testid="add-proposal-todo-btn"><Plus className="h-3 w-3 mr-1" /> Add To-Do</Button>
                    </div>

                    {todoFormOpen && (
                      <Card className="border-border border-dashed bg-transparent shadow-none">
                        <CardContent className="p-3 space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground">New To-Do</p>
                          <Input placeholder="Title *" value={todoForm.title} onChange={e => setTodoForm({ ...todoForm, title: e.target.value })} className="bg-background border-border" data-testid="proposal-todo-title" />
                          <RichTextEditor placeholder="Description (optional)" value={todoForm.description} onChange={v => setTodoForm({ ...todoForm, description: v })} collapsedHeight={80} />
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs text-muted-foreground">Assign To</Label>
                              <Select value={todoForm.assigned_to || '_none'} onValueChange={v => setTodoForm({ ...todoForm, assigned_to: v === '_none' ? '' : v })}>
                                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="_none" className="text-xs">Unassigned</SelectItem>
                                  {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={todoForm.due_date} onChange={e => setTodoForm({ ...todoForm, due_date: e.target.value })} className="bg-background border-border" /></div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Priority</Label>
                              <Select value={todoForm.priority} onValueChange={v => setTodoForm({ ...todoForm, priority: v })}>
                                <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  {['Low', 'Medium', 'High'].map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={createProposalTodo} className="bg-primary text-primary-foreground text-xs" data-testid="save-proposal-todo-btn"><Plus className="h-3 w-3 mr-1" /> Create To-Do</Button>
                            <Button size="sm" variant="outline" onClick={() => setTodoFormOpen(false)} className="text-xs">Cancel</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="space-y-1.5">
                      {proposalTodos.map(t => (
                        <div key={t.id} className={`flex items-start gap-2.5 p-2.5 rounded-md border ${t.status === 'Completed' ? 'border-border/50 bg-secondary/20 opacity-60' : 'border-border bg-background'}`} data-testid={`proposal-todo-${t.id}`}>
                          <Checkbox checked={t.status === 'Completed'} onCheckedChange={() => toggleProposalTodo(t.id, t.status)} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${t.status === 'Completed' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</p>
                            {t.description && <div className="mt-0.5"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                              {t.assignee_name && <span className="flex items-center gap-0.5"><User className="h-2.5 w-2.5" />{t.assignee_name}</span>}
                              {t.due_date && <span className="flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" />{t.due_date}</span>}
                              <Badge variant="outline" className={`text-xs ${t.priority === 'High' ? 'text-destructive border-destructive/20' : t.priority === 'Medium' ? 'text-amber-400 border-amber-500/20' : ''}`}>{t.priority}</Badge>
                            </div>
                          </div>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0" onClick={async () => { if (!window.confirm('Delete this to-do?')) return; try { await api.delete(`/tasks/${t.id}`); toast.success('Deleted'); fetchProposalTodos(); } catch { toast.error('Error'); } }}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      ))}
                      {proposalTodos.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No to-dos yet.</p>}
                    </div>
                  </TabsContent>
                )}

                {/* Time Logged Tab */}
                <TabsContent value="time-logged" className="mt-3 space-y-3">
                  <div className="flex items-center justify-between"><p className="text-xs text-muted-foreground">{(selected.time_entries || []).length} timesheet entries</p><Badge className="bg-primary/10 text-primary border-primary/20">{selected.total_hours_logged || 0} total hours</Badge></div>
                  {(selected.time_entries || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6">No time logged against this proposal yet</p>
                  ) : (
                    <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                      {(selected.time_entries || []).map(t => (
                        <Card key={t.id} className="border-border bg-background shadow-none" data-testid={`time-entry-${t.id}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-mono text-muted-foreground">{t.date}</span>
                                  <span className="text-sm font-bold text-primary">{t.hours}h</span>
                                  <Badge className={`text-xs ${t.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400' : t.status === 'Submitted' ? 'bg-amber-500/10 text-amber-400' : t.status === 'Rejected' ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-muted-foreground'}`}>{t.status}</Badge>
                                  {!t.is_billable && <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{t.user_name}</p>
                                {t.description && <div className="mt-0.5"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Status Log Tab */}
                <TabsContent value="status-log" className="mt-3" data-testid="proposal-status-log-tab-content">
                  <EntityStatusLogTab
                    entityType="proposal"
                    entityId={selected.id}
                    entityLabel={`Proposal ${selected.proposal_number || ''}`}
                    statusLog={selected.status_log || []}
                    onChange={() => refreshSelected()}
                  />
                </TabsContent>

                {/* Attachments Tab */}
                <TabsContent value="attachments" className="mt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">{proposalAttachments.length} attachment(s)</p>
                    <Button size="sm" onClick={() => { setPropAttachFormOpen(!propAttachFormOpen); setPropAttachName(''); setPropAttachUploaded(null); }} className="bg-primary text-primary-foreground" data-testid="add-proposal-attachment-btn"><Upload className="h-3 w-3 mr-1" /> Attach File</Button>
                  </div>
                  {propAttachFormOpen && (
                    <Card className="border-border border-dashed bg-transparent shadow-none">
                      <CardContent className="p-3 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground">New Attachment</p>
                        <FileUpload onUploadComplete={(data) => { setPropAttachUploaded(data); if (!propAttachName && data.file_name) setPropAttachName(data.file_name.replace(/\.[^/.]+$/, '')); }} />
                        {propAttachUploaded && (
                          <>
                            <div><Label className="text-xs text-muted-foreground">Document Name</Label><Input value={propAttachName} onChange={e => setPropAttachName(e.target.value)} className="bg-background border-border" placeholder="Document name" data-testid="proposal-attachment-name-input" /></div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={async () => {
                                if (!propAttachName.trim()) return toast.error('Name required');
                                try {
                                  await api.post('/documents', { name: propAttachName, category: 'Other', proposal_id: selected.id, file_url: propAttachUploaded.file_url, file_id: propAttachUploaded.file_id, file_name: propAttachUploaded.file_name, file_size: propAttachUploaded.file_size, content_type: propAttachUploaded.content_type });
                                  toast.success('Attachment added');
                                  setPropAttachFormOpen(false); setPropAttachUploaded(null); setPropAttachName('');
                                  fetchProposalAttachments();
                                } catch { toast.error('Error adding attachment'); }
                              }} className="bg-primary text-primary-foreground" data-testid="save-proposal-attachment-btn"><Plus className="h-3 w-3 mr-1" /> Save</Button>
                              <Button size="sm" variant="outline" onClick={() => { if (propAttachUploaded?.file_id) api.delete(`/upload/${propAttachUploaded.file_id}`).catch(() => {}); setPropAttachFormOpen(false); setPropAttachUploaded(null); }}>Cancel</Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  {proposalAttachments.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No attachments yet</p>
                  ) : (
                    proposalAttachments.map(d => {
                      const fullUrl = d.file_url ? (d.file_url.startsWith('http') ? d.file_url : `${process.env.REACT_APP_BACKEND_URL}${d.file_url}`) : null;
                      return (
                        <div key={d.id} className="flex items-center justify-between py-2 px-2 border border-border rounded-md hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => setPreviewPropAttachment(d)} data-testid={`proposal-attachment-${d.id}`}>
                          <div className="flex items-center gap-2 flex-1 min-w-0"><Paperclip className="h-4 w-4 text-muted-foreground shrink-0" /><div className="min-w-0"><p className="text-sm font-medium truncate">{d.name}</p><p className="text-xs text-muted-foreground">{d.file_name || ''}{d.file_size ? ` · ${(d.file_size / 1024).toFixed(0)} KB` : ''}</p></div></div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={(e) => { e.stopPropagation(); setPreviewPropAttachment(d); }} data-testid={`preview-proposal-attachment-${d.id}`}><Eye className="h-3 w-3" /></Button>
                            {fullUrl && <a href={fullUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}><Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"><Download className="h-3 w-3" /></Button></a>}
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Remove this attachment?')) return; try { await api.delete(`/documents/${d.id}`); toast.success('Removed'); fetchProposalAttachments(); } catch { toast.error('Error removing'); } }} data-testid={`delete-proposal-attachment-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </TabsContent>

              </Tabs>
            </div>
          )}

          <DocumentPreviewModal open={!!previewPropAttachment} onClose={() => setPreviewPropAttachment(null)} doc={previewPropAttachment} />
        </SheetContent>
      </Sheet>

      {/* Inline Create Contact Dialog */}
      <Dialog open={!!contactDialog} onOpenChange={(o) => { if (!o) setContactDialog(null); }}>
        <DialogContent className="bg-card border-border" data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle>New {contactDialog === 'primary' ? 'Primary' : 'Billable'} Contact</DialogTitle>
            <DialogDescription>This contact will be saved to the Contacts table and linked to this proposal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-muted-foreground">Name *</Label><Input value={newContactForm.name} onChange={e => setNewContactForm(f => ({ ...f, name: e.target.value }))} className="bg-background border-border" autoFocus data-testid="new-contact-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={newContactForm.email} onChange={e => setNewContactForm(f => ({ ...f, email: e.target.value }))} className="bg-background border-border" data-testid="new-contact-email" /></div>
              <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={newContactForm.phone} onChange={e => setNewContactForm(f => ({ ...f, phone: e.target.value }))} className="bg-background border-border" data-testid="new-contact-phone" /></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Job Title</Label><Input value={newContactForm.job_title} onChange={e => setNewContactForm(f => ({ ...f, job_title: e.target.value }))} className="bg-background border-border" data-testid="new-contact-title" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialog(null)}>Cancel</Button>
            <Button onClick={createContactAndLink} className="bg-primary text-primary-foreground" data-testid="create-contact-submit">Create & Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DD Quote Recovery Dialog - Owner/Admin only, for legacy DD proposals advanced past Engage Client */}
      <Dialog open={recoverDdOpen} onOpenChange={setRecoverDdOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="dd-recovery-dialog">
          <DialogHeader>
            <DialogTitle>Recover Due Diligence Quote</DialogTitle>
            <DialogDescription className="text-xs">
              This will set the quote fields on the proposal and {selected?.dd_quote?.invoice_id ? 'sync them onto the existing Draft invoice' : 'generate a Draft DD invoice ready for billing'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Quote Description</Label>
              <Input
                value={recoverDdForm.quote_description}
                onChange={e => setRecoverDdForm({ ...recoverDdForm, quote_description: e.target.value })}
                placeholder={`Due Diligence Research Fee for ${selected?.proposal_number || ''}`}
                className="bg-background border-border"
                data-testid="dd-recover-description"
              />
              <p className="text-xs text-muted-foreground mt-1">Falls back to the standard &quot;Due Diligence Research Fee&quot; label if blank.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Quote Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={recoverDdForm.quote_amount}
                onChange={e => setRecoverDdForm({ ...recoverDdForm, quote_amount: e.target.value })}
                placeholder="0.00"
                className="bg-background border-border"
                data-testid="dd-recover-amount"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!recoverDdForm.quote_sent}
                onCheckedChange={(v) => setRecoverDdForm({ ...recoverDdForm, quote_sent: !!v })}
                data-testid="dd-recover-quote-sent"
              />
              <Label className="text-xs">Quote was already sent to the client</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoverDdOpen(false)}>Cancel</Button>
            <Button onClick={submitRecoverDd} className="bg-primary text-primary-foreground" data-testid="dd-recover-submit">
              <DollarSign className="h-3 w-3 mr-1" /> Recover Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
